import React from 'react';
import { useDispatch } from 'react-redux';
import FeedbackPopover from './FeedbackPopover';
import { EntryFeedback } from '../store/conversationSlice';
import { updateLandmineFeedback } from '../store/slices/landmineSlice';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';
import { ALERT_BADGE_LABEL, LANDMINE_PALETTE, speakerLabel } from '../constants/alertTheme';
import { AlertShell, AlertBulletList, AlertParagraph, AlertSection } from './alerts/AlertShell';
import EarlierTag from './EarlierTag';
import { useAlertViewer, useIsWatcher } from '../contexts/SessionViewerContext';

interface LandmineAlertType {
  trigger: string;
  matched_text: string;
  matched_phrase: string;  // The phrase that semantically matched
  similarity_score: number;  // Confidence 0.0-1.0 (e.g., 0.85 = 85% match)
  semantic_match: boolean;  // True for LLM match
  landmine_type?: 'legal_risk' | 'detractor';  // Type of landmine
  category?: string;  // Only for legal_risk type
  priority: 'high' | 'medium' | 'low';
  can_say?: Array<{
    point: string;
    context?: string;
  }>;
  cannot_say?: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  quick_tips?: string;
  detected_at: string;
  speaker: string;
  feedback?: EntryFeedback;
  /** Restored from the saved session: marked "Earlier". */
  isRestored?: boolean;
}

interface LandmineAlertProps {
  alert: LandmineAlertType;
  /** Omitted in the Landmines accordion (LandmineBar), which keeps every landmine. */
  onDismiss?: () => void;
  /** Set by LandmineBar: the card is its own accordion (see AlertShell). */
  onToggleCollapse?: () => void;
  isCollapsed?: boolean;
}

export const LandmineAlert: React.FC<LandmineAlertProps> = ({ alert, onDismiss, onToggleCollapse, isCollapsed }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  // Feedback writes to the owner's session: a watcher only reads the alert.
  const isWatcher = useIsWatcher();
  // "You said" only for the viewer's own landmine; anyone else's names its raiser.
  const viewer = useAlertViewer();

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so UI reflects the choice even if persist fails
    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString()
    };
    dispatch(updateLandmineFeedback({ detected_at: alert.detected_at, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'landmine',
        entryTimestamp: alert.detected_at,
        rating,
        reason
      });
    } catch (err) {
      console.warn('[LandmineAlert] Entry feedback persist failed:', err);
    }
  };



  /**
   * EVERYTHING A REP CANNOT ACT ON IS GONE (owner, 2026-09-09).
   *
   * This card is read mid-call with a customer waiting, so the only questions
   * it may answer are: what set this off, who said it, and what do I do now.
   * Removed on that rule: the similarity/confidence percentage, the matched
   * phrase, the category, the severity chips, the priority icon, and the
   * show/hide toggle that hid the guidance a rep opened the alert to read.
   *
   * The old card also SPLIT by `landmine_type` — a detractor rendered "Say This
   * Instead" while a legal risk rendered a tip plus a collapsed section. A rep
   * mid-call does not care which internal type fired; they care what to say. So
   * both types render one shape now, and `quick_tips` leads it because for a
   * detractor that string IS the replacement line.
   */
  const canSay = alert.can_say?.filter((item) => item?.point) ?? [];
  const cannotSay = alert.cannot_say?.filter((item) => item?.point) ?? [];

  return (
    <AlertShell
      palette={LANDMINE_PALETTE}
      badgeLabel={ALERT_BADGE_LABEL.landmine}
      name={alert?.trigger}
      speaker={speakerLabel(alert, viewer)}
      trigger={alert?.matched_text}
      onDismiss={onDismiss}
      onToggleCollapse={onToggleCollapse}
      isCollapsed={isCollapsed}
      feedback={
        <span className="flex items-center gap-2">
          {alert.isRestored && <EarlierTag />}
          {!isWatcher && <FeedbackPopover currentFeedback={alert.feedback} onSubmit={handleFeedbackSubmit} />}
        </span>
      }
    >
      {alert.quick_tips && (
        <AlertSection palette={LANDMINE_PALETTE} title="Quick Tips">
          <AlertParagraph palette={LANDMINE_PALETTE}>{alert.quick_tips}</AlertParagraph>
        </AlertSection>
      )}

      {canSay.length > 0 && (
        <AlertSection palette={LANDMINE_PALETTE} title="You can say">
          <AlertBulletList
            palette={LANDMINE_PALETTE}
            items={canSay.map((item) => item.point)}
          />
        </AlertSection>
      )}

      {cannotSay.length > 0 && (
        <AlertSection palette={LANDMINE_PALETTE} title="You cannot say">
          <AlertBulletList
            palette={LANDMINE_PALETTE}
            items={cannotSay.map((item) => item.point)}
            marker="cross"
          />
        </AlertSection>
      )}
    </AlertShell>
  );
};
